import { useEffect } from 'react';
import first from 'lodash/first';
import last from 'lodash/last';
import { getFocusableElements } from '../utils/focusableElements';

/**
 * Traps the focus within the referenced element. I.e. the focus will never leave the element by tabbing.
 * @param {*} ref
 */
export default function useFocusTrap(ref) {
    useEffect(() => {
        if (!ref?.current) return;

        const focusableElements = getFocusableElements(ref.current);
        if (!focusableElements.length) return;

        /**
         * There's an un-handled edge case here: we never update the references to the first and last elements,
         * so if elements are added or enabled after initial render, the references might be stale.
         */
        const firstFocusableElement = first(focusableElements);
        const lastFocusableElement = last(focusableElements);

        const handleKeyDownFirst = e => {
            if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                lastFocusableElement.focus();
            }
        };
        const handleKeyDownLast = e => {
            if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                firstFocusableElement.focus();
            }
        };
        const handleKeyDownWindow = e => {
            if (e.key === 'Tab' && e.target === document.body && !e.shiftKey) {
                e.preventDefault();
                firstFocusableElement.focus();
            } else if (
                e.key === 'Tab' &&
                e.target === document.body &&
                e.shiftKey
            ) {
                e.preventDefault();
                lastFocusableElement.focus();
            }
        };

        firstFocusableElement.addEventListener(
            'keydown',
            handleKeyDownFirst,
            false
        );
        lastFocusableElement.addEventListener(
            'keydown',
            handleKeyDownLast,
            false
        );
        window.addEventListener('keydown', handleKeyDownWindow, false);

        return () => {
            firstFocusableElement.removeEventListener(
                'keydown',
                handleKeyDownFirst,
                false
            );
            lastFocusableElement.removeEventListener(
                'keydown',
                handleKeyDownLast,
                false
            );
            window.removeEventListener('keydown', handleKeyDownWindow, false);
        };
    }, [ref]);
}
